import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import Message from '../../model/Message'
import { dataSource, notificationService, storageService } from '../../service/domain/_service.config'
import { ImageData, embedImagesInText } from '../../util/ImageUtil'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useUser } from '../user/useUser'
import { MESSAGES_QUERY_KEY } from './useMessages'

export function useSendMessage() {
  const queryClient = useQueryClient()
  const { data: user } = useUser()
  const { teamID } = useChallengeAccess()

  return useMutation({
    mutationFn: async ({ 
      messageText, 
      replyToId, 
      images,
      files
    }: { 
      messageText: string; 
      replyToId?: string; 
      images?: ImageData[];
      files?: File[];
    }) => {
      if (!user) throw new Error("Can't send message without a user")
      if (!teamID) throw new Error('User must be in a team to send messages')

      const messageId = uuidv4()
      let finalMessageText = messageText

      // Handle image uploads if any
      if (files && files.length > 0) {
        const uploadedImages: ImageData[] = []

        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const imageData = images?.[i] // Get corresponding image data
          
          // Upload to storage
          const uploadedUrl = await storageService.uploadMessageImage(file, user.id, messageId)
          uploadedImages.push({
            url: uploadedUrl,
            alt: imageData?.alt || file.name,
          })
        }

        // Embed images in message text
        finalMessageText = embedImagesInText(messageText, uploadedImages)
      }

      const message = new Message({
        id: messageId,
        user: user,
        teamID,
        message: finalMessageText,
        createdAt: new Date(),
        replyToId: replyToId,
      })

      // Save the message
      await dataSource.saveMessage(message)

      // Notify the rest of the team
      const team = await dataSource.getTeam(teamID)
      if (team) {
        await notificationService.sendChatMessageNotification(message, team.members)
      }

      return message
    },
    onSuccess: (message) => {
      queryClient.invalidateQueries({
        queryKey: MESSAGES_QUERY_KEY(message.teamID),
      })
    },
  })
}
